import { DtVersionSelection, LinkedDtProject } from "../types";

export const DT_VERSION_ALL = "all";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Parses the `dtVersions` URL value ("<linkId>:<all|versionUuid>,...").
 * Malformed entries are dropped so a hand-edited URL never breaks the page.
 */
export function parseDtVersions(raw: string | null): DtVersionSelection {
  const selection: DtVersionSelection = {};
  if (!raw) return selection;
  for (const pair of raw.split(",")) {
    const [linkId, value, ...rest] = pair.trim().split(":");
    if (rest.length > 0 || !linkId || !value || !UUID_RE.test(linkId)) {
      continue;
    }
    if (value === DT_VERSION_ALL || UUID_RE.test(value)) {
      selection[linkId] = value;
    }
  }
  return selection;
}

export function serializeDtVersions(selection: DtVersionSelection): string {
  return Object.keys(selection)
    .sort()
    .map((linkId) => `${linkId}:${selection[linkId]}`)
    .join(",");
}

/**
 * Keeps only choices that still apply to the given projects, e.g. dropping a
 * pinned version that no longer exists or an unlinked project.
 */
export function sanitizeDtVersions(
  selection: DtVersionSelection,
  projects: LinkedDtProject[],
): DtVersionSelection {
  const out: DtVersionSelection = {};
  for (const project of projects) {
    const value = selection[project.linkId];
    if (
      value === DT_VERSION_ALL ||
      (value && project.versions.some((v) => v.uuid === value))
    ) {
      out[project.linkId] = value;
    }
  }
  return out;
}

export function describeDtVersions(
  selection: DtVersionSelection,
  projects: LinkedDtProject[],
): string {
  const pinned = Object.keys(sanitizeDtVersions(selection, projects)).length;
  if (pinned === 0) return "Versions: Latest";
  return `Versions: ${pinned} of ${projects.length} pinned`;
}

export function formatBomImport(lastBomImport: number): string {
  return lastBomImport > 0
    ? new Date(lastBomImport).toLocaleDateString()
    : "no BOM";
}
